import {Image} from '@primer/react-brand'
import {AutoPlayVideo} from '../AutoPlayVideo/AutoPlayVideo'
import type {GenericAsset} from '../../../../../../brand/lib/types/contentful'

import Styles from './MediaAsset.module.css'

type Props = {
  content: GenericAsset[]
  location: string
  loading?: 'eager' | 'lazy'
}

export function MediaAsset(props: Props) {
  const {content, location, loading = 'lazy'} = props

  // Media assets added in Contentful are shown based on the following priority:

  // 1. Whatever assets are added first in Contentful are preferred.
  // 2. If a video AND an image is available, the first video will be shown and the first image is used as the video's "poster" image.
  //    Make sure the video and poster image have the same orientation, resolution and first frame matches.

  const isImageAsset = (asset: GenericAsset): boolean => {
    return !!asset.fields.asset.fields.file.details?.image
  }

  // Separate assets into images and videos
  const images = content.filter(isImageAsset)
  const videos = content.filter(asset => !isImageAsset(asset))

  // Select the appropriate image based on orientation and viewport
  let selectedImage: GenericAsset['fields'] | undefined
  if (images.length > 0) {
    const finalImage = images[0]
    selectedImage = finalImage?.fields
  }

  // Select the appropriate video and poster based on orientation and viewport
  let selectedVideo: GenericAsset['fields'] | undefined
  let selectedPoster: GenericAsset['fields'] | undefined
  if (videos.length > 0) {
    const finalVideo = videos[0]
    selectedVideo = finalVideo?.fields

    // Use an image as poster if available
    if (images.length > 0) {
      const finalPoster = images[0]
      selectedPoster = finalPoster?.fields
    }
  }

  const image = selectedImage
  const video = selectedVideo
  const poster = selectedPoster

  return (
    <div className={Styles.MediaAsset}>
      {video ? (
        <AutoPlayVideo
          src={video.asset.fields.file.url}
          poster={poster?.asset.fields.file.url || ''}
          width={video.width || ''}
          height={video.height || ''}
          aria-label={video.description || ''}
          analyticsProps={{
            context: 'video',
            location,
          }}
        />
      ) : image ? (
        <Image
          className={Styles.Image}
          src={image.asset.fields.file.url}
          width={image.width || image.asset.fields.file.details?.image?.width || ''}
          height={image.height || image.asset.fields.file.details?.image?.height || ''}
          alt={image.asset.fields.description || ''}
          loading={loading}
        />
      ) : null}
    </div>
  )
}
